import React from "react";
import { useFormContext } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Node, Edge } from "@xyflow/react";
import AiConversationFlowBuilder from "../components/AiConversationFlowBuilder";
import CollapsibleCard from "./CollapsibleCard";
import { TemplateFormValues } from "../templateForm";

export default function TemplateConversationSection({
  flowNodes,
  flowEdges,
  onFlowChange,
  showFlowBuilder = true,
  collapseAdvanced = false,
}: {
  flowNodes: Node[];
  flowEdges: Edge[];
  onFlowChange: (nodes: Node[], edges: Edge[]) => void;
  showFlowBuilder?: boolean;
  collapseAdvanced?: boolean;
}) {
  const form = useFormContext<TemplateFormValues>();
  const isNative = form.watch("orchestrator") === "NATIVE";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Greeting and Closing</CardTitle>
          <CardDescription>How agents on this template start and wrap up a call</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField control={form.control} name="defaultGreeting" render={({ field }) => (
            <FormItem>
              <FormLabel>Default Greeting</FormLabel>
              <FormControl>
                <Input placeholder="Hello! How can I help you today?" {...field} />
              </FormControl>
            </FormItem>
          )} />

          <FormField control={form.control} name="defaultClosingMessage" render={({ field }) => (
            <FormItem>
              <FormLabel>Default Closing Message</FormLabel>
              <FormControl>
                <Textarea
                  rows={2}
                  placeholder="Thanks for your time. We'll send a confirmation by email shortly."
                  {...field}
                />
              </FormControl>
              <FormDescription>
                How agents on this template wrap up a call that ends well. Injected into the prompt as
                guidance (not read out verbatim), and skipped when a call ends badly. Org users can
                override it per agent.
              </FormDescription>
            </FormItem>
          )} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AI Behaviour</CardTitle>
          <CardDescription>
            Controls how the agent responds to sensitive questions. Agents using this template will inherit these settings unless overridden per-agent.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField control={form.control} name="aiDisclosureEnabled" render={({ field }) => (
            <FormItem>
              <FormLabel>AI Disclosure</FormLabel>
              <Select
                value={field.value === null || field.value === undefined ? "null" : String(field.value)}
                onValueChange={(value) => field.onChange(value === "null" ? null : value === "true")}
              >
                <FormControl><SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value="null">Inherit from platform config</SelectItem>
                  <SelectItem value="true">Enabled: agent admits it's AI when asked</SelectItem>
                  <SelectItem value="false">Disabled: agent maintains human persona</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                Controls how the agent responds if a caller asks whether they are talking to a real person.
              </FormDescription>
            </FormItem>
          )} />
        </CardContent>
      </Card>

      {isNative && (
        <CollapsibleCard
          title="Silence Handling"
          description="Controls what happens when the caller stops responding mid-call. Leave blank to use platform defaults."
          collapsed={collapseAdvanced}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField control={form.control} name="silencePromptTimeoutSec" render={({ field }) => (
              <FormItem>
                <FormLabel>Re-prompt delay (seconds)</FormLabel>
                <FormControl>
                  <Input
                    type="number" min={1} max={60} placeholder="7 (default)"
                    value={field.value ?? ""}
                    onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Seconds of user silence after the agent finishes speaking before asking "Are you still there?"
                </FormDescription>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="silenceHangupTimeoutSec" render={({ field }) => (
              <FormItem>
                <FormLabel>Hangup timeout (seconds)</FormLabel>
                <FormControl>
                  <Input
                    type="number" min={5} max={300} placeholder="30 (default)"
                    value={field.value ?? ""}
                    onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Seconds of total user silence before ending the call with a no-response outcome.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )} />
          </div>
          <FormField control={form.control} name="silencePromptPhrase" render={({ field }) => (
            <FormItem>
              <FormLabel>Re-prompt phrase</FormLabel>
              <FormControl>
                <Input placeholder="Are you still there? (default)" {...field} value={field.value ?? ""} />
              </FormControl>
              <FormDescription>
                What the agent says when the caller has been silent for too long.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="waitPhraseTimeoutSec" render={({ field }) => (
            <FormItem className="max-w-xs">
              <FormLabel>Wait-phrase hold window (seconds)</FormLabel>
              <FormControl>
                <Input
                  type="number" min={5} max={120} placeholder="20 (default)"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))}
                />
              </FormControl>
              <FormDescription>
                How long to suppress the re-prompt after the caller says "hold on" or "give me a sec". Extend for agents where callers often need to look things up.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
        </CollapsibleCard>
      )}

      <CollapsibleCard
        title="Conversation tuning (advanced)"
        description="Default tuning for agents created from this template. These are admin-only: they can be overridden per agent from the Deployed Agents page, not by org members. Leave blank to use the platform default."
        collapsed={collapseAdvanced}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={form.control} name="temperature" render={({ field }) => (
            <FormItem>
              <FormLabel>Temperature (0 to 1)</FormLabel>
              <FormControl>
                <Input type="number" step="0.1" min={0} max={1} placeholder="0.7 (default)"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseFloat(event.target.value))} />
              </FormControl>
              <FormDescription>
                Response randomness. Lower is more focused and consistent (better for scripted qualification); higher is more varied and creative.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="endpointingMs" render={({ field }) => (
            <FormItem>
              <FormLabel>Endpointing (ms)</FormLabel>
              <FormControl>
                <Input type="number" min={50} max={3000} placeholder="200 (default)"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))} />
              </FormControl>
              <FormDescription>
                How long the caller must pause before the agent assumes they've finished. Lower is snappier but more risk of cutting people off; higher is calmer.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="turnHoldMs" render={({ field }) => (
            <FormItem>
              <FormLabel>Continuation hold (ms)</FormLabel>
              <FormControl>
                <Input type="number" min={0} max={3000} placeholder="400 (default)"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))} />
              </FormControl>
              <FormDescription>
                Extra wait for the caller to keep talking before responding, so a trailing "um, maybe spring" isn't answered early.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="minCallDurationSec" render={({ field }) => (
            <FormItem>
              <FormLabel>Minimum call duration (seconds)</FormLabel>
              <FormControl>
                <Input type="number" min={0} max={600} placeholder="45 (default)"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))} />
              </FormControl>
              <FormDescription>
                Prevents hanging up too early; auto-hangup is blocked before this unless the caller clearly ends or the goal is met.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="maxDurationSeconds" render={({ field }) => (
            <FormItem>
              <FormLabel>Maximum call duration (seconds)</FormLabel>
              <FormControl>
                <Input type="number" min={0} max={7200} placeholder="no cap"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))} />
              </FormControl>
              <FormDescription>
                Hard cap on call length as a safety stop. Blank means no cap.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )} />
        </div>
      </CollapsibleCard>

      {showFlowBuilder && (
        <Card>
          <CardHeader>
            <CardTitle>Conversation Flow</CardTitle>
            <CardDescription>Design the conversation tree for this agent</CardDescription>
          </CardHeader>
          <CardContent>
            <AiConversationFlowBuilder
              initialNodes={flowNodes}
              initialEdges={flowEdges}
              onChange={onFlowChange}
            />
          </CardContent>
        </Card>
      )}
    </>
  );
}
